<template>
  <div class="content">
    <!-- Nagłówek białej listy -->
    <h3 class="center-aligned">{{ t('white-list').toUpperCase() }}:</h3>
    <div class="form center-aligned">
      <!-- Formularz ręcznego dodawania pojazdu -->
      <div class="form-group">
        <label for="licensePlate" class="label-license-plate">{{ t('plate-no') }}:</label>
        <input id="licensePlate" v-model="licensePlate" type="text" />
      </div>
      <div class="form-group">
        <label for="description" class="label-description">{{ t('vehicle-description') }}:</label>
        <input id="description" v-model="description" type="text" maxlength="100" />
      </div>
      <!-- Przypisywanie użytkownika tylko dla osób z dostępem -->
      <div v-if="hasAccess('UserManagement')" class="form-group">
        <label for="user" class="label-user">Przypisz użytkownika:</label>
        <select id="user" v-model="selectedUserId">
          <option value="">Przypisz użytkownika</option>
          <option v-for="user in users"
                  :key="user.id"
                  :value="user.id">
            {{ user.username }}
          </option>
        </select>
      </div>
      <!-- Przycisk dodawania pojazdu -->
      <button @click="addPlate" :disabled="!licensePlate || !description" class="button-save">{{ t('add') }}</button>
      
      <div>
        {{ t('or') }}
      </div>
      
      <!-- Wgrywanie pojazdów z pliku CSV -->
      <div class="file-upload center-aligned">
        <input type="file" @change="handleFileUpload" accept=".csv" ref="fileInput"/>
        <div>
          <span v-if="uploadedFileName">{{ uploadedFileName }} <button @click="removeFile" class="button-delete">X</button></span>
        </div>

        <!-- Tabela pojazdów z pliku CSV gotowych do wysyłki -->
        <div v-if="csvVehicles.length > 0">
          <h4>{{ t('ready-to-send-csv-vehicles') }}:</h4>
          <table class="csv-vehicle-table center-aligned">
            <thead>
              <tr>
                <th>{{ t('ordinal-number') }}</th>
                <th>{{ t('short-plate-no') }}</th>
                <th v-if="hasAccess('UserManagement')">Przypisany użytkownik</th>
                <th>{{ t('vehicle-description') }}</th>
                <th>{{ t('action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(vehicle, index) in csvVehicles" :key="vehicle.id">
                <td>{{ index + 1 }}</td>
                <td>{{ vehicle.licensePlate }}</td>
                <td v-if="hasAccess('UserManagement')">
                  <select :id="`user-${vehicle.id}`" v-model="vehicle.userId">
                    <option value="">Przypisz użytkownika</option>
                    <option v-for="user in users"
                            :key="user.id"
                            :value="user.id">
                      {{ user.username }}
                    </option>
                  </select>
                </td>
                <td>{{ vehicle.description }}</td>
                <td><button @click="removeCSVVehicle(index)" class="button-delete">{{ t('delete') }}</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Przycisk do wysyłki wszystkich pojazdów z CSV -->
      <button v-if="csvVehicles.length > 0" @click="submitCSVVehicles" class="button-save">{{ t('add-vehicles-from-csv') }}</button>
    </div>

    <!-- Tabela pojazdów na białej liście -->
    <div v-if="vehicles.length > 0">
      <table class="vehicle-table center-aligned">
        <thead>
          <tr>
            <th>{{ t('ordinal-number') }}</th>
            <th>{{ t('short-plate-no') }}</th>
            <th>Przypisany użytkownik</th>
            <th>{{ t('hits-no') }}</th>
            <th>{{ t('monitored-since') }}</th>
            <th>{{ t('vehicle-description') }}</th>
            <th>{{ t('action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(vehicle, index) in paginatedVehicles" :key="vehicle.id">
            <td>{{ index + 1 }}</td>
            <td>{{ vehicle.licensePlate }}</td>
            <td>{{ vehicle.username }}</td>
            <td>{{ vehicle.hits }}</td>
            <td>{{ formatDateTime(vehicle.monitoredSince) }}</td>
            <td>{{ vehicle.description }}</td>
            <td><button @click="removeVehicle(vehicle.id)" class="button-delete">{{ t('delete') }}</button></td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else>
      <p class="center-aligned">{{ t('no-vehicles-on-white-list') }}</p>
    </div>
  </div>
</template>

<script>
import WhiteListsService from "../services/WhiteListsService";
import Papa from 'papaparse'; // Biblioteka do parsowania CSV
import { useUserStore } from "@/stores/user.store";

export default {
  name: 'WhiteList',
  data() {
    return {
      licensePlate: '',      // Wartość pola numer rejestracyjny
      description: '',       // Wartość pola opis
      vehicles: [],          // Lista wszystkich pojazdów na białej liście
      csvVehicles: [],       // Lista pojazdów wgranych z CSV
      uploadedFileName: '',  // Nazwa wgranego pliku CSV
      currentPage: 1,        // Numer aktualnej strony (paginacja)
      pageSize: 5,           // Ilość rekordów na stronie
      users: [],             // Lista użytkowników do przypisywania pojazdu
      selectedUserId: null,  // Wybrany użytkownik w formularzu dodawania
      username: ''
    };
  },
  computed: {
    // Zwraca tylko pojazdy z wybranego zakresu stron (paginacja)
    paginatedVehicles() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = this.currentPage * this.pageSize;
      return this.vehicles.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.vehicles.length / this.pageSize);
    }
  },
  methods: {
    // Sprawdza uprawnienia użytkownika
    hasAccess(id) {
      const userStore = useUserStore();
      return userStore.hasAccess(id);
    },
    // Pobiera użytkowników do przypisywania pojazdów
    async getUsers() {
      try {
        const response = await WhiteListsService.getUsers();
        this.users = response.map(u => ({
          id:       u.id,
          username: u.username
        }));
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    // Dodaje pojedynczy pojazd do białej listy (z formularza)
    async addPlate() {
      try {
        const response = await WhiteListsService.postNewPlate(
          this.licensePlate,
          this.description,
          this.selectedUserId
        );

        this.vehicles.push({
          licensePlate: this.licensePlate,
          description: this.description,
          hits: 0,
          monitoredSince: new Date().toISOString(),
          id: response.id,
          username: response.user.username
        });
        // Czyści formularz
        this.licensePlate = '';
        this.description = '';
        this.selectedUserId = null;

      } catch (error) {
        if (error.message === 'Both license plate and description are required.') {
          console.warn(error.message);
        } else {
          console.error('Error adding vehicle:', error);
        }
      }
    },
    // Pobiera wszystkie pojazdy z białej listy z backendu
    async fetchVehicles() {
      try {
        const response = await WhiteListsService.getAll();
        this.vehicles = response.map(vehicle => ({
          licensePlate: vehicle.plateLpr,
          description: vehicle.description,
          hits: vehicle.hits || 0,
          monitoredSince: vehicle.createdAt || new Date().toISOString(),
          id: vehicle.id,
          userId: vehicle.user.id,
          username: vehicle.user.username
        }));
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      }
    },
    // Usuwa pojazd z białej listy po ID
    async removeVehicle(id) {
      try {
        await WhiteListsService.delete(id);
        this.vehicles = this.vehicles.filter(vehicle => vehicle.id !== id);
      } catch (error) {
        console.error('Error deleting vehicle:', error);
      }
    },
    // Paginacja - poprzednia strona
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    // Paginacja - następna strona
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    // Formatowanie daty i godziny na czytelny zapis
    formatDateTime(dateString) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    },
    // Obsługa wgrywania pliku CSV z pojazdami
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadedFileName = file.name; // Ustaw nazwę pliku
        Papa.parse(file, {
          header: false,
          skipEmptyLines: true,
          delimiter: ';', // Ustaw delimiter na średnik
          complete: this.addVehiclesFromCSV,
          error: function (error) {
            console.error('Error reading CSV file:', error);
          }
        });
      }
    },
    // Dodaje pojazdy z pliku CSV do tymczasowej listy (do potwierdzenia wysyłki)
    addVehiclesFromCSV(results) {
      this.csvVehicles = results.data.map(vehicle => ({
        licensePlate: vehicle[0],
        description: vehicle[1],
        hits: 0,
        monitoredSince: new Date().toISOString(),
        id: Date.now() + Math.random(), // Unikalny ID dla pojazdu z CSV
        userId: null,
      }));
    },
    // Wysyła wszystkie pojazdy z CSV do backendu
    async submitCSVVehicles() {
      for (let vehicle of this.csvVehicles) {
        try {
          const response = await WhiteListsService.postNewPlate(
            vehicle.licensePlate,
            vehicle.description,
            vehicle.userId
          );

          this.vehicles.push({
            licensePlate: vehicle.licensePlate,
            description: vehicle.description,
            hits: 0,
            monitoredSince: new Date().toISOString(),
            id: response.id,
            username: response.user.username,
          });

        } catch (error) {
          console.error('Error adding vehicle from CSV:', error);
        }
      }
      // Czyści tymczasową listę po wysłaniu
      this.csvVehicles = [];
      this.uploadedFileName = '';
      this.$refs.fileInput.value = '';
    },
    // Usuwa plik CSV i listę pojazdów z CSV (reset)
    removeFile() {
      this.uploadedFileName = '';
      this.csvVehicles = [];
      this.$refs.fileInput.value = '';
    },
    // Usuwa pojedynczy pojazd z tymczasowej listy CSV
    removeCSVVehicle(index) {
      this.csvVehicles.splice(index, 1);
    },
  },
  // Po zamontowaniu komponentu pobiera użytkowników i pojazdy z API
  mounted() {
    this.getUsers();
    this.fetchVehicles();
  }
};
</script>
  
<style scoped>
h3 {
  padding: 1rem;
  font-weight: bold;
  border-bottom: 1px solid #aeaeae;
}

/* .form {
  margin-bottom: 20px;
  margin-top: 1.5rem;
  border-bottom: 1px solid #aeaeae;
}
*/
.form .form-group {
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 10rem;
}

.form .form-group .label-license-plate {
  padding-right: 0.5rem;
}

.form .form-group .label-description {
  padding-right: 3.77rem;
}

.form .form-group select {
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  cursor: pointer;
}

/* .form button {
  margin-top: 1rem;
  width: 194px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
  margin-bottom: 1.5rem;
} */

.vehicle-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.vehicle-table th,
.vehicle-table td {
  border: 1px solid #ccc;
  padding: 10px;
}

.vehicle-table td button {
  width: 75%;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination button {
  padding: 5px 10px;
  margin: 0 5px;
}

p {
  font-size: large;
}

.file-upload {
  margin: 20px 0;
}

.file-upload span {
  margin-left: 10px;
}

.file-upload button {
  background: none;
  border: none;
  color: red;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
}

.csv-vehicle-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.csv-vehicle-table th,
.csv-vehicle-table td {
  border: 1px solid #ccc;
  padding: 10px;
  /* text-align: center; */
}

.csv-vehicle-table td button {
  width: 75%;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
}

.button-delete {
  padding: 0.25rem 1rem 0.25rem 1rem;
}
</style>
